// --- Config -----------------------------------------------------------------
const EUROPEAN_REGIONS = new Set([
    'AD', 'AL', 'AT', 'AX', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES',
    'FI', 'FO', 'FR', 'GB', 'GG', 'GI', 'GR', 'HR', 'HU', 'IE', 'IM', 'IS', 'IT', 'JE', 'LI',
    'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'RU',
    'SE', 'SI', 'SJ', 'SK', 'SM', 'TR', 'UA', 'VA', 'XK',
]);

const GBP_REGIONS = new Set([
    'GB', 'GG', 'IM', 'JE',
]);

const CNY_REGIONS = new Set([
    'CN',
]);

const USD_REGIONS = new Set([
    'US',
]);

export type SupportedCurrency = 'GBP' | 'EUR' | 'USD' | 'CNY';

// --- Types ------------------------------------------------------------------
interface CurrencyOptions {
    acceptLanguage?: string;
    fallbackLocale?: string;
    localeTags?: string[];
    regionCode?: string | null;
    timeZone?: string | null;
    currencyOverride?: SupportedCurrency;
    formatOptions?: Intl.NumberFormatOptions;
}

interface LocaleOptions {
    acceptLanguage?: string;
    fallbackLocale?: string;
    localeTags?: string[];
}

interface CurrencyFormatter {
    locale: string;
    currency: SupportedCurrency;
    formatter: Intl.NumberFormat;
    format: (n: number) => string;
}

// --- Internals --------------------------------------------------------------
function normalizeLocaleTag(tag: string | null | undefined): string | null {
    if (typeof tag !== 'string') {
        return null;
    }

    const normalizedTag = tag
        .trim()
        .split(/[.@]/, 1)[0]
        ?.replace(/_/g, '-');

    if (!normalizedTag) {
        return null;
    }

    try {
        return Intl.getCanonicalLocales(normalizedTag)[0] ?? null;
    } catch {
        return null;
    }
}

export function parseRegionFromTag(tag: string): string | null {
    const normalizedTag = normalizeLocaleTag(tag);

    if (!normalizedTag) {
        return null;
    }

    try {
        const locale = new Intl.Locale(normalizedTag);
        if (locale.region) {
            return String(locale.region).toUpperCase();
        }
    } catch {
        // Ignore malformed locale tags and fall back to regex parsing.
    }

    const match = /[-_](\w{2,3})/.exec(normalizedTag);
    return match ? match[1].toUpperCase() : null;
}

function normalizeRegionCode(regionCode: string | null | undefined): string | null {
    if (typeof regionCode !== 'string') {
        return null;
    }

    const normalizedRegionCode = regionCode.trim().toUpperCase();
    return normalizedRegionCode.length > 0 ? normalizedRegionCode : null;
}

function getLocales({ acceptLanguage, fallbackLocale = 'en-US', localeTags }: LocaleOptions = {}): string[] {
    if (Array.isArray(localeTags) && localeTags.length > 0) {
        const normalizedLocaleTags = localeTags
            .map((localeTag) => normalizeLocaleTag(localeTag))
            .filter((localeTag): localeTag is string => localeTag !== null);

        if (normalizedLocaleTags.length > 0) {
            return normalizedLocaleTags;
        }
    }

    if (typeof navigator !== 'undefined') {
        const navigatorLocales = navigator.languages?.length ? navigator.languages : [navigator.language].filter(Boolean);
        if (navigatorLocales?.length) {
            const normalizedNavigatorLocales = Array.from(navigatorLocales)
                .map((localeTag) => normalizeLocaleTag(localeTag))
                .filter((localeTag): localeTag is string => localeTag !== null);

            if (normalizedNavigatorLocales.length > 0) {
                return normalizedNavigatorLocales;
            }
        }
    }

    if (acceptLanguage) {
        const normalizedAcceptLanguageLocales = acceptLanguage
            .split(',')
            .map((value: string) => value.trim())
            .map((value) => value.split(';')[0]?.trim() || '')
            .map((localeTag) => normalizeLocaleTag(localeTag))
            .filter((localeTag): localeTag is string => localeTag !== null);

        if (normalizedAcceptLanguageLocales.length > 0) {
            return normalizedAcceptLanguageLocales;
        }
    }

    return [normalizeLocaleTag(fallbackLocale) ?? 'en-US'];
}

function getSafeTimeZone(): string | null {
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return typeof timeZone === 'string' && timeZone.trim().length > 0 ? timeZone : null;
    } catch {
        return null;
    }
}

function inferRegionFromTimeZone(timeZone: string | null): string | null {
    if (!timeZone) {
        return null;
    }

    if (timeZone === 'Europe/London') {
        return 'GB';
    }

    if (timeZone === 'Asia/Shanghai' || timeZone === 'Asia/Urumqi') {
        return 'CN';
    }

    if (timeZone.startsWith('Europe/')) {
        return 'EU';
    }

    return null;
}

function getCurrencyForRegion(regionCode: string | null | undefined): SupportedCurrency | null {
    const normalizedRegionCode = normalizeRegionCode(regionCode);

    if (!normalizedRegionCode) {
        return null;
    }

    if (CNY_REGIONS.has(normalizedRegionCode)) {
        return 'CNY';
    }

    if (GBP_REGIONS.has(normalizedRegionCode)) {
        return 'GBP';
    }

    if (normalizedRegionCode === 'EU' || EUROPEAN_REGIONS.has(normalizedRegionCode)) {
        return 'EUR';
    }

    if (USD_REGIONS.has(normalizedRegionCode)) {
        return 'USD';
    }

    return null;
}

function getRepresentativeRegionForCurrency(currency: SupportedCurrency): string {
    switch (currency) {
        case 'CNY':
            return 'CN';
        case 'GBP':
            return 'GB';
        case 'EUR':
            return 'EU';
        case 'USD':
        default:
            return 'US';
    }
}

function getConsensusCurrencyFromLocaleTags(localeTags: string[]): SupportedCurrency | null {
    const votes = new Map<SupportedCurrency, number>();

    for (const localeTag of localeTags) {
        const currency = getCurrencyForRegion(parseRegionFromTag(localeTag));

        if (!currency) {
            continue;
        }

        votes.set(currency, (votes.get(currency) ?? 0) + 1);
    }

    const rankedVotes = Array.from(votes.entries())
        .sort(([, leftCount], [, rightCount]) => rightCount - leftCount);
    const topVote = rankedVotes[0];
    const runnerUpVote = rankedVotes[1];

    if (!topVote || (runnerUpVote && runnerUpVote[1] === topVote[1])) {
        return null;
    }

    return topVote[0];
}

function getPrimaryLocaleCurrency({
    acceptLanguage,
    fallbackLocale,
    localeTags,
    regionCode,
}: CurrencyOptions): SupportedCurrency | null {
    const regionCurrency = getCurrencyForRegion(regionCode);

    if (regionCurrency) {
        return regionCurrency;
    }

    const primaryLocale = getLocales({ acceptLanguage, fallbackLocale, localeTags })[0];
    return primaryLocale ? getCurrencyForRegion(parseRegionFromTag(primaryLocale)) : null;
}

export function inferRegionFromHints({
    localeTags,
    regionCode,
    timeZone,
    acceptLanguage,
    fallbackLocale,
}: CurrencyOptions = {}): string | null {
    const timeZoneRegion = inferRegionFromTimeZone(timeZone ?? getSafeTimeZone());

    if (getCurrencyForRegion(timeZoneRegion)) {
        return timeZoneRegion;
    }

    const locales = getLocales({ localeTags, acceptLanguage, fallbackLocale });
    const consensusCurrency = getConsensusCurrencyFromLocaleTags(locales);

    if (consensusCurrency) {
        return getRepresentativeRegionForCurrency(consensusCurrency);
    }

    const normalizedRegionCode = normalizeRegionCode(regionCode);

    if (normalizedRegionCode) {
        return normalizedRegionCode;
    }

    const primaryLocaleRegion = locales[0] ? parseRegionFromTag(locales[0]) : null;

    if (primaryLocaleRegion) {
        return primaryLocaleRegion;
    }

    return timeZoneRegion;
}

export function getPreferredCurrency({
    regionCode,
    timeZone,
}: {
    regionCode?: string | null;
    timeZone?: string | null;
} = {}): SupportedCurrency {
    const normalizedRegionCode = normalizeRegionCode(regionCode);
    const inferredRegionCode = normalizedRegionCode ?? inferRegionFromTimeZone(timeZone ?? null);
    return getCurrencyForRegion(inferredRegionCode) ?? 'USD';
}

// --- Public API -------------------------------------------------------------

/**
 * Determine a probable preferred currency bucket.
 */
export function probableCurrency(opts: CurrencyOptions = {}): SupportedCurrency {
    const timeZoneCurrency = getCurrencyForRegion(inferRegionFromTimeZone(opts.timeZone ?? getSafeTimeZone()));

    if (timeZoneCurrency) {
        return timeZoneCurrency;
    }

    const locales = getLocales(opts);
    const consensusCurrency = getConsensusCurrencyFromLocaleTags(locales);

    if (consensusCurrency) {
        return consensusCurrency;
    }

    return getPrimaryLocaleCurrency(opts) ?? 'USD';
}

/**
 * Build an Intl.NumberFormat for the detected currency.
 */
export function makeCurrencyFormatter(opts: CurrencyOptions = {}): CurrencyFormatter {
    const locales = getLocales(opts);
    const locale = locales[0] || 'en-US';
    const currency = opts.currencyOverride || probableCurrency(opts);
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: undefined,
        maximumFractionDigits: undefined,
        ...opts.formatOptions,
    });

    return {
        locale,
        currency,
        formatter,
        format: (n: number) => formatter.format(n),
    };
}

/**
 * Format a value immediately (one-liner convenience).
 */
export function formatCurrency(amount: number, opts: CurrencyOptions = {}): string {
    return makeCurrencyFormatter(opts).format(amount);
}
